/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description ICD Parser
 */
import _ from 'lodash';

const ICD10_SINGLE = /^[A-Z]\d[0-9A-Z](?:\.[A-Z0-9]{1,4})?[A-Z]?$/i;
const ICD10_DASH = /^[A-Z]\d[0-9A-Z](?:\.[A-Z0-9]{0,4})?-\s*$/i;
const ICD10_RANGE = /^[A-Z]\d[0-9A-Z](?:\.[A-Z0-9]{1,4})?-[A-Z]\d[0-9A-Z](?:\.[A-Z0-9]{1,4})?$/i;

const getTruncatedText = (text: string, maxLength: number): string => {
	if (_.size(text) <= maxLength) {
		return text;
	}
	return text?.slice(0, maxLength) + '...';
};

const getDigitRequired = (
	code: string,
	requiredCharLength: number,
	hipaaSupport: boolean,
): { text: string; title: string; isBillable: boolean } => {
	const codeLevel = _.size(code);
	const SEVENTH_CHARACTER = 7;

	if (hipaaSupport || requiredCharLength == null) {
		return {
			text: 'B',
			title: 'Billable code',
			isBillable: true,
		};
	}

	if (requiredCharLength !== SEVENTH_CHARACTER || (requiredCharLength === SEVENTH_CHARACTER && requiredCharLength - codeLevel === 1)) {
		return {
			text: `${requiredCharLength}th`,
			title: `${requiredCharLength}th character required`,
			isBillable: false,
		};
	}

	if (requiredCharLength - codeLevel !== 1) {
		return {
			text: 'x7th',
			title: '7th character required with placeholder X',
			isBillable: false,
		};
	}

	return {
		text: '',
		title: '',
		isBillable: false,
	};
};

const isValidICD10Code = (code: string): boolean => {
	return ICD10_SINGLE.test(code) || ICD10_DASH.test(code) || ICD10_RANGE.test(code);
};

const separateTextAndCode = (text: string): CodeExplorer.ILink => {
	const lastOpenParenIndex = _.lastIndexOf(text, '(');
	const lastCloseParenIndex = _.lastIndexOf(text, ')');
	if (lastOpenParenIndex === -1 || lastCloseParenIndex !== _.size(text) - 1) {
		return { text, link: [] };
	}

	const textBeforeCode = _.trim(text?.substring(0, lastOpenParenIndex));
	const parenthesisContent = _.trim(text?.substring(lastOpenParenIndex + 1, lastCloseParenIndex));

	const codes = _.map(_.split(parenthesisContent, ','), (item) => _.trim(item)).filter(isValidICD10Code);

	return {
		text: _.isEmpty(codes) ? text : textBeforeCode,
		link: codes,
	};
};

const parseDescription = (description: string): CodeExplorer.ILink => {
	const { text, link } = separateTextAndCode(description);
	return { text, link: link || [] };
};

const parseLinkAndText = (data: string[]): CodeExplorer.ILink[] => {
	return _.map(data, (item) => separateTextAndCode(item));
};

const parseGuidelinesExcludesIncludes = (guidelines: CodeExplorer.IICD10CMDetails): CodeExplorer.ILinkMap => {
	return {
		excludes1: parseLinkAndText(guidelines?.excludes1 || []),
		excludes2: parseLinkAndText(guidelines?.excludes2 || []),
		includes: parseLinkAndText(guidelines?.includes || []),
	};
};

const parseCodeGuidelines = (codeguidelines: CodeExplorer.IICD10CMDetails): CodeExplorer.IGuidelines => {
	if (!codeguidelines) {
		return null;
	}

	return {
		description: parseDescription(codeguidelines.desc) ?? null,
		inclusionTerm: codeguidelines.inclusionterm || [],
		useAdditionalCode: parseLinkAndText(codeguidelines.useadditionalcode || []),
		codeFirst: parseLinkAndText(codeguidelines.codefirst || []),
		codeAlso: parseLinkAndText(codeguidelines.codealso || []),
		includeExcludes: parseGuidelinesExcludesIncludes(codeguidelines),
		sevenCharacterNote: !_.isEmpty(codeguidelines.sevenchrnote) ? _.first(codeguidelines.sevenchrnote) : null,
		sevenCharacterDef: codeguidelines.sevenchrdef || null,
		notes: codeguidelines.notes || null,
	};
};

const parseChapterGuidelines = (chapterguidelines: CodeExplorer.IICD10CMDetails): CodeExplorer.IGuidelines => {
	if (!chapterguidelines) {
		return null;
	}

	return {
		description: parseDescription(chapterguidelines.desc),
		includeExcludes: parseGuidelinesExcludesIncludes(chapterguidelines),
		useAdditionalCode: parseLinkAndText(chapterguidelines.useadditionalcode || []),
		codeFirst: parseLinkAndText(chapterguidelines.codefirst || []),
		codeAlso: parseLinkAndText(chapterguidelines.codealso || []),
		notes: chapterguidelines.notes || null,
	};
};

const parseSectionGuidelines = (sectionguidelines: CodeExplorer.IICD10CMDetails): CodeExplorer.IGuidelines => {
	if (!sectionguidelines) {
		return null;
	}

	return {
		description: parseDescription(sectionguidelines.desc),
		inclusionTerm: sectionguidelines.inclusionterm || [],
		includeExcludes: parseGuidelinesExcludesIncludes(sectionguidelines),
		useAdditionalCode: parseLinkAndText(sectionguidelines.useadditionalcode || []),
		codeFirst: parseLinkAndText(sectionguidelines.codefirst || []),
		codeAlso: parseLinkAndText(sectionguidelines.codealso || []),
		notes: sectionguidelines.notes || null,
	};
};

// clinicalcondition, layterm will be handled in the future
const icdDataParser = (codeDetails: CodeExplorer.IICD10CMCodeDetails): CodeExplorer.ICDParser => {
	const {
		chapterguidelines,
		sectionguidelines,
		codeguidelines,
		// clinicalcondition,
		// layterm
	} = codeDetails?.detail ?? {};

	return {
		codeGuidelines: parseCodeGuidelines(codeguidelines),
		chapterGuidelines: parseChapterGuidelines(chapterguidelines),
		sectionGuidelines: parseSectionGuidelines(sectionguidelines),
		// clinicalCondition: clinicalcondition || [],
		// layTerm: layterm || [],
	};
};

export { icdDataParser, getTruncatedText, getDigitRequired };
